import { appConfig, buildAppFunctorWorkspaceNetworkErrorMessage } from '../config/appConfig';
import type { Asset, KnowledgeParticle } from '../types/domain';
import { safeExtractText } from '../utils/textHelper';
import { assetService } from './assetService';
import { apiClient } from './apiClient';

export type WorkspaceGraphRoot = {
  kind: 'asset' | 'particle';
  title: string;
  assetId?: string;
  particleId?: string;
  surface?: WorkspaceGraphSurface;
};

export type WorkspaceGraphSurfaceKind = 'image' | 'audio' | 'video' | 'unknown';

export type WorkspaceGraphSurface = {
  kind: WorkspaceGraphSurfaceKind;
  previewUrl?: string;
};

export type WorkspaceGraphAnchorKind = 'image_point' | 'audio_time' | 'video_frame_point';

export type WorkspaceGraphAnchor = {
  kind: WorkspaceGraphAnchorKind;
  source: 'fallback' | 'user' | 'extracted';
  x?: number;
  y?: number;
  t?: number;
  updatedAt?: string;
};

export type WorkspaceGraphNode = {
  id: string;
  kind: 'asset' | 'particle' | 'derived';
  title: string;
  subtitle?: string;
  metadata?: Record<string, unknown>;
  imageUrl?: string;
  assetId?: string;
  particleId?: string;
  artifactId?: string;
  parentNodeId?: string;
  parentArtifactId?: string;
  sourceParticleId?: string;
  action?: string;
  status?: string;
  summary?: string;
  question?: string;
  answer?: string;
  sessionId?: string;
  resumeSessionId?: string;
  reactorTurnId?: string;
  localInputState?: 'draft' | 'suggested' | 'sending' | 'failed';
  localSourceTitle?: string;
  localErrorMessage?: string;
  localSuggestedPrompts?: string[];
  localBindingLabel?: string;
  localBindingTitle?: string;
  localBindingMessage?: string;
  localBindingTone?: 'loading' | 'success' | 'danger' | 'neutral';
};

export type WorkspaceGraphEdge = {
  id: string;
  source: string;
  target: string;
  relation: 'asset_particle' | 'particle_derived';
  anchor?: WorkspaceGraphAnchor;
};

export type WorkspaceGraphBundle = {
  root: WorkspaceGraphRoot;
  nodes: WorkspaceGraphNode[];
  edges: WorkspaceGraphEdge[];
};

export type WorkspaceSessionMessage = {
  id?: string;
  turnId?: string;
  role: 'user' | 'assistant' | 'system';
  action?: string;
  content: string;
  createdAt: string;
  payload?: Record<string, unknown>;
};

export type WorkspaceDerivedItem = {
  id: string;
  title: string;
  summary: string;
  why?: string;
  createdAt: string;
  source: 'history' | 'session';
  action?: string;
  sessionId?: string;
  resumeSessionId?: string;
  reactorTurnId?: string;
  sourceParticleId?: string;
  parentArtifactId?: string;
  particleId?: string;
  question?: string;
  answer?: string;
  status?: string;
  promotionRecord?: Record<string, unknown>;
};

export type WorkspaceSessionPayload = {
  session?: {
    id: string;
    focusParticleId: string;
    particleTitle: string;
    source: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  };
  recentMessages?: WorkspaceSessionMessage[];
  sessionArtifacts?: WorkspaceDerivedItem[];
  currentAction?: string | null;
};

export type WorkspaceSessionMutationResponse = {
  data?: WorkspaceSessionPayload;
  artifactId?: string;
  sessionArtifacts?: WorkspaceDerivedItem[];
  replay?: {
    editedMessageId?: string;
    replacedAssistantMessageId?: string;
    deletedDraftArtifactCount?: number;
    keptArtifactCount?: number;
  };
  deletion?: {
    artifactId?: string;
    status?: string;
    particleId?: string;
    deletedDraftArtifactIds?: string[];
    keptArtifactCount?: number;
  };
  error?: string;
  message?: string;
};

export type WorkspaceTurnResponse = {
  reactionPreview?: Record<string, unknown>;
  runtime?: Record<string, unknown>;
  sessionArtifacts?: WorkspaceDerivedItem[];
  kpActionApplications?: Record<string, unknown>[];
  persistedTurn?: {
    userMessage?: WorkspaceSessionMessage;
    assistantMessage?: WorkspaceSessionMessage;
  };
  buildSession?: WorkspaceSessionPayload['session'];
  error?: string;
  message?: string;
};

type ApiResponse<T> = {
  data?: T;
  error?: string;
  message?: string;
};

type DerivedItemCountsResponse = ApiResponse<Record<string, number>>;
type AssetParticleAnchorResponse = ApiResponse<{
  assetId: string;
  particleId: string;
  anchor: WorkspaceGraphAnchor;
}>;

type WorkspaceRequestHeaderOptions = {
  includeJsonContentType?: boolean;
};

const KPWORKSPACE_AUTH_ERROR_MESSAGE = '关系图鉴权失败，请重新登录后再试';

function readText(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
}

function readPayloadText(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value && typeof value === 'object') {
    return safeExtractText(value);
  }

  return '';
}

function truncateText(value: unknown, maxLength: number): string {
  const text = readText(value).replace(/\s+/g, ' ').trim();
  if (!text) {
    return '';
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, Math.max(maxLength - 1, 1)).trimEnd()}...`;
}

function readFirstLine(value: unknown, maxLength: number): string {
  const text = readPayloadText(value)
    .split(/\r?\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .find(Boolean);

  if (!text) {
    return '';
  }

  return truncateText(text, maxLength);
}

function looksLikeTechnicalAssetTitle(value: string, assetId?: string): boolean {
  const normalized = value.replace(/\s+/g, '').toLowerCase();
  const normalizedAssetId = readText(assetId).replace(/\s+/g, '').toLowerCase();

  if (!normalized) {
    return true;
  }

  if (normalizedAssetId && normalized === normalizedAssetId) {
    return true;
  }

  return normalized.includes('/assets/')
    || normalized.includes('oss-cn-')
    || normalized.startsWith('http://')
    || normalized.startsWith('https://')
    || (/^[a-z0-9_-]{12,}$/i.test(normalized) && !/[\u4e00-\u9fff]/.test(value));
}

function pickAssetTitleCandidate(value: unknown, assetId?: string, maxLength = 34): string {
  const candidate = readFirstLine(value, maxLength);
  if (!candidate || looksLikeTechnicalAssetTitle(candidate, assetId)) {
    return '';
  }

  return candidate;
}

function resolveAssetSurfaceKind(asset: Pick<Asset, 'type' | 'url'>): WorkspaceGraphSurfaceKind {
  const type = readText(asset.type).toLowerCase();
  const url = readText(asset.url).toLowerCase();

  if (type.startsWith('image') || /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?.*)?$/i.test(url)) {
    return 'image';
  }

  if (type.startsWith('audio') || /\.(mp3|wav|aac|m4a|ogg)(\?.*)?$/i.test(url)) {
    return 'audio';
  }

  if (type.startsWith('video') || /\.(mp4|mov|m4v|webm)(\?.*)?$/i.test(url)) {
    return 'video';
  }

  return 'unknown';
}

function buildFallbackAssetSurface(asset: Asset): WorkspaceGraphSurface | undefined {
  const previewUrl = readText(asset.url);
  const kind = resolveAssetSurfaceKind(asset);

  if (!previewUrl && kind === 'unknown') {
    return undefined;
  }

  return {
    kind,
    previewUrl: previewUrl || undefined,
  };
}

function buildFallbackImageAnchor(index: number, total: number): WorkspaceGraphAnchor {
  const denominator = Math.max(total + 1, 2);
  return {
    kind: 'image_point',
    source: 'fallback',
    x: 0.98,
    y: Math.min(Math.max((index + 1) / denominator, 0), 1),
  };
}

function buildFallbackAssetTitle(asset: Asset): string {
  return pickAssetTitleCandidate(asset.ai_title, asset.id)
    || pickAssetTitleCandidate(asset.description, asset.id)
    || pickAssetTitleCandidate(asset.ai_brief, asset.id)
    || pickAssetTitleCandidate(asset.subject, asset.id, 24)
    || '未命名素材';
}

function buildFallbackAssetSubtitle(asset: Asset): string {
  const parts = [truncateText(asset.subject, 18), truncateText(asset.type, 18)].filter(Boolean);
  return parts.join(' · ') || '素材';
}

function buildFallbackParticleTitle(particle: KnowledgeParticle): string {
  return truncateText(particle.title, 64)
    || readFirstLine(particle.questions, 64)
    || readFirstLine(particle.answers, 64)
    || '未命名知识颗粒';
}

function buildFallbackParticleSubtitle(particle: KnowledgeParticle, title: string): string {
  const question = readFirstLine(particle.questions, 96);
  if (question && question !== title) {
    return question;
  }

  return truncateText(particle.subject, 24) || '知识颗粒';
}

function buildFallbackAssetGraphNode(asset: Asset, linkedParticleCount: number): WorkspaceGraphNode {
  return {
    id: `asset:${asset.id}`,
    kind: 'asset',
    title: buildFallbackAssetTitle(asset),
    subtitle: buildFallbackAssetSubtitle(asset),
    imageUrl: readText(asset.url) || undefined,
    assetId: asset.id,
    status: linkedParticleCount > 0 ? 'linked' : 'empty',
    summary: truncateText(asset.ai_brief || asset.description, 180) || undefined,
  };
}

function buildFallbackParticleGraphNode(particle: KnowledgeParticle): WorkspaceGraphNode {
  const title = buildFallbackParticleTitle(particle);
  const question = readPayloadText(particle.questions) || undefined;
  const answer = readPayloadText(particle.answers) || undefined;

  return {
    id: `particle:${particle.id}`,
    kind: 'particle',
    title,
    subtitle: buildFallbackParticleSubtitle(particle, title),
    particleId: particle.id,
    status: 'idle',
    question,
    answer,
  };
}

async function buildFallbackAssetWorkspaceGraph(assetId: string): Promise<WorkspaceGraphBundle> {
  const asset = await assetService.getAssetWithAIExtractions(assetId).catch(() => assetService.getAsset(assetId));
  const particles = await assetService.listKnowledgeParticlesByAsset(assetId);
  const surface = buildFallbackAssetSurface(asset);
  const nodes: WorkspaceGraphNode[] = [
    buildFallbackAssetGraphNode(asset, particles.length),
    ...particles.map((particle) => buildFallbackParticleGraphNode(particle)),
  ];
  const edges: WorkspaceGraphEdge[] = particles.map((particle, index) => ({
    id: `edge:asset:${asset.id}:particle:${particle.id}`,
    source: `asset:${asset.id}`,
    target: `particle:${particle.id}`,
    relation: 'asset_particle',
    anchor: surface?.kind === 'image' ? buildFallbackImageAnchor(index, particles.length) : undefined,
  }));

  return {
    root: {
      kind: 'asset',
      title: buildFallbackAssetTitle(asset),
      assetId: asset.id,
      surface,
    },
    nodes,
    edges,
  };
}

function isAuthFailure(payload: ApiResponse<unknown> | null, status: number): boolean {
  const errorCode = typeof payload?.error === 'string' ? payload.error.trim().toLowerCase() : '';
  return status === 401
    || errorCode === 'token_invalid'
    || errorCode === 'token_expired'
    || errorCode === 'token_revoked';
}

function throwKpWorkspaceAuthError(): never {
  throw new Error(KPWORKSPACE_AUTH_ERROR_MESSAGE);
}

export async function buildKpWorkspaceHeaders(options: WorkspaceRequestHeaderOptions = {}): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (options.includeJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }

  const accessToken = await apiClient.getAccessToken();
  if (accessToken) {
    headers[appConfig.accessHeaderName] = `Bearer ${accessToken}`;
    return headers;
  }

  return throwKpWorkspaceAuthError();
}

async function parseJsonPayload<T>(response: Response): Promise<ApiResponse<T> | null> {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as ApiResponse<T>;
  } catch {
    return null;
  }
}

async function getWorkspaceGraphBundle(pathname: string): Promise<WorkspaceGraphBundle> {
  const url = `${appConfig.kpWorkspaceBaseUrl}${pathname}`;
  let response: Response;
  try {
    response = await fetch(url, {
      headers: await buildKpWorkspaceHeaders(),
    });
  } catch {
    throw new Error(buildAppFunctorWorkspaceNetworkErrorMessage(pathname));
  }
  const payload = await parseJsonPayload<WorkspaceGraphBundle>(response);

  if (isAuthFailure(payload, response.status)) {
    return throwKpWorkspaceAuthError();
  }

  if (!response.ok || !payload?.data) {
    throw new Error(payload?.message || payload?.error || `读取 KpWorkspace 关系图失败 (HTTP ${response.status})`);
  }

  return payload.data;
}

async function getWorkspacePayload(pathname: string): Promise<WorkspaceSessionPayload> {
  const url = `${appConfig.kpWorkspaceBaseUrl}${pathname}`;
  let response: Response;
  try {
    response = await fetch(url, {
      headers: await buildKpWorkspaceHeaders(),
    });
  } catch {
    throw new Error(buildAppFunctorWorkspaceNetworkErrorMessage(pathname));
  }
  const payload = await parseJsonPayload<WorkspaceSessionPayload>(response);

  if (isAuthFailure(payload, response.status)) {
    return throwKpWorkspaceAuthError();
  }

  if (!response.ok || !payload?.data) {
    throw new Error(payload?.message || payload?.error || `读取 KpWorkspace 会话失败 (HTTP ${response.status})`);
  }

  return payload.data;
}

async function mutateWorkspacePayload(pathname: string, options: RequestInit): Promise<WorkspaceSessionMutationResponse> {
  const url = `${appConfig.kpWorkspaceBaseUrl}${pathname}`;
  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        ...(await buildKpWorkspaceHeaders({ includeJsonContentType: options.body ? true : undefined })),
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new Error(buildAppFunctorWorkspaceNetworkErrorMessage(pathname));
  }
  const payload = await parseJsonPayload<WorkspaceSessionPayload>(response) as WorkspaceSessionMutationResponse | null;

  if (isAuthFailure(payload as ApiResponse<unknown> | null, response.status)) {
    return throwKpWorkspaceAuthError();
  }

  if (!response.ok) {
    throw new Error(payload?.error || payload?.message || `KpWorkspace 请求失败 (HTTP ${response.status})`);
  }

  return payload || {};
}

async function getDerivedItemCounts(focusParticleIds: string[]): Promise<Record<string, number>> {
  const normalizedIds = Array.from(new Set(focusParticleIds.map((id) => id.trim()).filter(Boolean)));
  if (!normalizedIds.length) {
    return {} as Record<string, number>;
  }

  const derivedCountsUrl = `${appConfig.kpWorkspaceBaseUrl}/api/workspace/derived-item-counts`;
  let response: Response;
  try {
    response = await fetch(derivedCountsUrl, {
      method: 'POST',
      headers: await buildKpWorkspaceHeaders({ includeJsonContentType: true }),
      body: JSON.stringify({ focusParticleIds: normalizedIds }),
    });
  } catch {
    throw new Error(buildAppFunctorWorkspaceNetworkErrorMessage('/api/workspace/derived-item-counts'));
  }
  const payload = await parseJsonPayload<Record<string, number>>(response);

  if (isAuthFailure(payload, response.status)) {
    return throwKpWorkspaceAuthError();
  }

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `读取衍生知识颗粒数量失败 (HTTP ${response.status})`);
  }

  return payload?.data ?? ({} as Record<string, number>);
}

export const kpWorkspaceService = {
  getDerivedItemCounts,
  buildFallbackAssetGraph: buildFallbackAssetWorkspaceGraph,

  async getAssetWorkspaceGraph(assetId: string): Promise<WorkspaceGraphBundle> {
    const normalizedId = assetId.trim();
    if (!normalizedId) {
      throw new Error('assetId is required');
    }

    return getWorkspaceGraphBundle(`/api/workspace/graph/assets/${encodeURIComponent(normalizedId)}`);
  },

  async getParticleWorkspaceGraph(particleId: string): Promise<WorkspaceGraphBundle> {
    const normalizedId = particleId.trim();
    if (!normalizedId) {
      throw new Error('particleId is required');
    }

    return getWorkspaceGraphBundle(`/api/workspace/graph/particles/${encodeURIComponent(normalizedId)}`);
  },

  async updateAssetParticleAnchor(input: {
    assetId: string;
    particleId: string;
    anchor: WorkspaceGraphAnchor;
  }): Promise<WorkspaceGraphAnchor> {
    const assetId = input.assetId.trim();
    const particleId = input.particleId.trim();

    if (!assetId || !particleId) {
      throw new Error('assetId and particleId are required');
    }

    const response = await fetch(
      `${appConfig.kpWorkspaceBaseUrl}/api/workspace/graph/assets/${encodeURIComponent(assetId)}/particles/${encodeURIComponent(particleId)}/anchor`,
      {
        method: 'PUT',
        headers: await buildKpWorkspaceHeaders({ includeJsonContentType: true }),
        body: JSON.stringify({
          anchor: {
            kind: input.anchor.kind,
            x: input.anchor.x,
            y: input.anchor.y,
            t: input.anchor.t,
          },
        }),
      },
    );
    const payload = await parseJsonPayload<AssetParticleAnchorResponse['data']>(response) as AssetParticleAnchorResponse | null;

    if (isAuthFailure(payload, response.status)) {
      return throwKpWorkspaceAuthError();
    }

    if (!response.ok || !payload?.data?.anchor) {
      throw new Error(payload?.message || payload?.error || `更新素材标注点失败: ${response.status}`);
    }

    return payload.data.anchor;
  },

  async getDerivedItemCount(focusParticleId: string): Promise<number> {
    const normalizedId = focusParticleId.trim();
    if (!normalizedId) {
      return 0;
    }

    const counts = await getDerivedItemCounts([normalizedId]);
    return counts[normalizedId] || 0;
  },

  async getWorkspaceSession(sessionId: string): Promise<WorkspaceSessionPayload> {
    const normalizedId = sessionId.trim();
    if (!normalizedId) {
      throw new Error('sessionId is required');
    }

    return getWorkspacePayload(`/api/workspace/sessions/${encodeURIComponent(normalizedId)}`);
  },

  async rewriteWorkspaceSessionMessage(input: {
    sessionId: string;
    messageId: string;
    focusParticleId: string;
    particleTitle?: string;
    content: string;
  }): Promise<WorkspaceSessionMutationResponse> {
    const sessionId = input.sessionId.trim();
    const messageId = input.messageId.trim();
    const focusParticleId = input.focusParticleId.trim();
    const content = input.content.trim();

    if (!sessionId || !messageId || !focusParticleId || !content) {
      throw new Error('sessionId, messageId, focusParticleId and content are required');
    }

    return mutateWorkspacePayload(
      `/api/workspace/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/rewrite`,
      {
        method: 'POST',
        body: JSON.stringify({
          focusParticleId,
          particleTitle: input.particleTitle || '未命名知识颗粒',
          content,
        }),
      },
    );
  },

  async deleteSessionArtifact(input: {
    sessionId: string;
    artifactId: string;
  }): Promise<WorkspaceSessionMutationResponse> {
    const sessionId = input.sessionId.trim();
    const artifactId = input.artifactId.trim();

    if (!sessionId || !artifactId) {
      throw new Error('sessionId and artifactId are required');
    }

    return mutateWorkspacePayload(
      `/api/workspace/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`,
      {
        method: 'DELETE',
      },
    );
  },

  async promoteSessionDraft(input: {
    sessionId: string;
    focusParticleId: string;
    artifactId: string;
    targetParticle: {
      id: string;
      title: string;
      questions: string;
      answers: string;
      subject: string;
      createdAt: string;
    };
    promotionRecord?: Record<string, unknown>;
  }): Promise<WorkspaceSessionMutationResponse> {
    const sessionId = input.sessionId.trim();
    const focusParticleId = input.focusParticleId.trim();
    const artifactId = input.artifactId.trim();

    if (!sessionId || !focusParticleId || !artifactId) {
      throw new Error('sessionId, focusParticleId and artifactId are required');
    }

    return mutateWorkspacePayload(
      `/api/workspace/sessions/${encodeURIComponent(sessionId)}/promote-draft`,
      {
        method: 'POST',
        body: JSON.stringify({
          focusParticleId,
          artifactId,
          targetParticle: input.targetParticle,
          promotionRecord: input.promotionRecord,
        }),
      },
    );
  },

  async bindSessionParticle(input: {
    sessionId: string;
    focusParticleId: string;
    targetParticle: {
      id: string;
      title: string;
      questions: string;
      answers: string;
      subject: string;
      createdAt: string;
    };
  }): Promise<WorkspaceSessionMutationResponse> {
    const sessionId = input.sessionId.trim();
    const focusParticleId = input.focusParticleId.trim();

    if (!sessionId || !focusParticleId || !input.targetParticle?.id?.trim()) {
      throw new Error('sessionId, focusParticleId and targetParticle.id are required');
    }

    return mutateWorkspacePayload(
      `/api/workspace/sessions/${encodeURIComponent(sessionId)}/bind-particle`,
      {
        method: 'POST',
        body: JSON.stringify({
          focusParticleId,
          targetParticle: input.targetParticle,
        }),
      },
    );
  },

  async submitWorkspaceTurn(input: {
    workspaceSessionId?: string;
    clientMessageId?: string;
    focusParticleId: string;
    particleTitle?: string;
    content: string;
    suggestedActions?: string[];
    graphNodeMode?: boolean;
    parentArtifactId?: string;
    expansionRequest?: {
      strategyId: string;
      quantity: number;
      customPrompt?: string;
      provenanceVersion: 'kp-expansion-v1';
    };
    graphDraftLimit?: number;
  }): Promise<WorkspaceTurnResponse> {
    const focusParticleId = input.focusParticleId.trim();
    const content = input.content.trim();
    if (!focusParticleId || !content) {
      throw new Error('focusParticleId and content are required');
    }

    const response = await fetch(`${appConfig.kpWorkspaceBaseUrl}/api/workspace/turns`, {
      method: 'POST',
      headers: await buildKpWorkspaceHeaders({ includeJsonContentType: true }),
      body: JSON.stringify({
        workspaceSessionId: input.workspaceSessionId,
        clientMessageId: input.clientMessageId,
        focusParticleId,
        particleTitle: input.particleTitle || '未命名知识颗粒',
        input: {
          mode: 'text',
          content,
        },
        suggestedActions: input.suggestedActions || [],
        graphMode: input.graphNodeMode ? {
          singleDraftOnly: true,
          parentArtifactId: input.parentArtifactId,
          draftLimit: input.graphDraftLimit,
          expansionRequest: input.expansionRequest,
        } : undefined,
      }),
    });
    const payload = await parseJsonPayload<WorkspaceTurnResponse>(response);

    if (isAuthFailure(payload, response.status)) {
      return throwKpWorkspaceAuthError();
    }

    if (!response.ok) {
      throw new Error(payload?.error || payload?.message || `Reactor 反馈失败: ${response.status}`);
    }

    return payload?.data || (payload as WorkspaceTurnResponse | null) || {};
  },

  async submitAssetWorkspaceTurn(input: {
    workspaceSessionId?: string;
    clientMessageId?: string;
    assetId: string;
    assetTitle?: string;
    content: string;
    suggestedActions?: string[];
    graphNodeMode?: boolean;
    parentArtifactId?: string;
  }): Promise<WorkspaceTurnResponse> {
    const assetId = input.assetId.trim();
    const content = input.content.trim();
    if (!assetId || !content) {
      throw new Error('assetId and content are required');
    }

    const response = await fetch(`${appConfig.kpWorkspaceBaseUrl}/api/workspace/turns`, {
      method: 'POST',
      headers: await buildKpWorkspaceHeaders({ includeJsonContentType: true }),
      body: JSON.stringify({
        workspaceSessionId: input.workspaceSessionId,
        clientMessageId: input.clientMessageId,
        focus: {
          assetId,
          kind: 'asset',
          title: input.assetTitle || '素材',
        },
        input: {
          mode: 'text',
          content,
        },
        suggestedActions: input.suggestedActions || [],
        graphMode: input.graphNodeMode ? {
          singleDraftOnly: true,
          parentArtifactId: input.parentArtifactId,
        } : undefined,
      }),
    });
    const payload = await parseJsonPayload<WorkspaceTurnResponse>(response);

    if (isAuthFailure(payload, response.status)) {
      return throwKpWorkspaceAuthError();
    }

    if (!response.ok) {
      throw new Error(payload?.error || payload?.message || `素材直提 Reactor 反馈失败: ${response.status}`);
    }

    return payload?.data || (payload as WorkspaceTurnResponse | null) || {};
  },
};